import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Label, Row, Col, Spinner } from 'reactstrap';
import gridApi from '../../api/gridApi';
import { showConfirm, showSuccess, showError, showWarning } from '../../../utils/sweetAlertHelper';

function UserOrdersControl() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  const [cancelType, setCancelType] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('app_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL }/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('No se pudieron cargar los usuarios. Intenta recargar la página.');
      setUsers([]);
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    const user = users.find(u => u._id === userId);
    setSelectedUserName(user ? user.nombre : '');
  };

  const handleCancelOrders = async () => {
    if (!selectedUser) {
      await showWarning('Debes seleccionar un usuario antes de continuar.', 'Usuario Requerido');
      return;
    }

    const typeText = cancelType === 'ALL' ? 'TODAS las órdenes' :
                     cancelType === 'BUY' ? 'las órdenes de COMPRA' :
                     'las órdenes de VENTA';
    
    const confirmation = await showConfirm(
      `¿Estás seguro que quieres cancelar ${typeText} de ${selectedSymbol} para ${selectedUserName}?\n\nEsta acción NO se puede deshacer.`,
      '⚠️ Confirmar Cancelación',
      { confirmButtonText: 'Sí, cancelar órdenes' }
    );
    
    if (!confirmation.isConfirmed) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await gridApi.cancelUserOrders({
        userId: selectedUser,
        symbol: selectedSymbol,
        type: cancelType
      });

      const errorInfo = result.errors.length > 0 ? `\nErrores: ${result.errors.length}` : '';
      await showSuccess(
        `Canceladas: ${result.cancelled}\nFallidas: ${result.failed}${errorInfo}`,
        '✅ Órdenes Canceladas Exitosamente'
      );

      // Reset form
      setSelectedUser('');
      setSelectedUserName('');
      setCancelType('ALL');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setError(errorMsg);
      await showError(errorMsg, 'Error al Cancelar Órdenes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="bg-dark text-white">
        <h5 className="mb-0">🗑️ Cancelación de Órdenes por Usuario</h5>
      </CardHeader>
      <CardBody>
        {error && (
          <div className="alert alert-warning">
            <strong>⚠️ Advertencia:</strong> {error}
          </div>
        )}

        <Row className="mb-3">
          <Col md={4}>
            <Label>Usuario: <span className="text-danger">*</span></Label>
            <Input
              type="select"
              value={selectedUser}
              onChange={handleUserChange}
              disabled={loading}
              bsSize="sm"
            >
              <option value="">Seleccionar usuario...</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.nombre} ({user.email})
                </option>
              ))}
            </Input>
          </Col>

          <Col md={4}>
            <Label>Símbolo: <span className="text-danger">*</span></Label>
            <Input
              type="select"
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              disabled={loading}
              bsSize="sm"
            >
              <option value="BTCUSDT">BTCUSDT</option>
              <option value="ETHUSDT">ETHUSDT</option>
              <option value="BNBUSDT">BNBUSDT</option>
              <option value="SOLUSDT">SOLUSDT</option>
              <option value="ADAUSDT">ADAUSDT</option>
            </Input>
          </Col>

          <Col md={4}>
            <Label>Tipo de órdenes: <span className="text-danger">*</span></Label>
            <Input
              type="select"
              value={cancelType}
              onChange={(e) => setCancelType(e.target.value)}
              disabled={loading}
              bsSize="sm"
            >
              <option value="ALL">Todas (BUY + SELL)</option>
              <option value="BUY">Solo COMPRA</option>
              <option value="SELL">Solo VENTA</option>
            </Input>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12} className="d-grid">
            <Button
              onClick={handleCancelOrders}
              disabled={loading || !selectedUser}
              color="danger"
              size="sm"
            >
              {loading ? <><Spinner size="sm" /> Cancelando...</> : '🗑️ Cancelar Órdenes'}
            </Button>
          </Col>
        </Row>

        <div className="alert alert-warning">
          <p className="mb-2"><strong>⚠️ Advertencia Importante:</strong></p>
          <ul className="mb-0">
            <li>Esta acción cancelará las órdenes en Binance y <strong>NO se puede deshacer</strong></li>
            <li>Las órdenes canceladas se marcarán como "CANCELLED" en la base de datos</li>
            <li>El usuario recibirá una notificación de Telegram (si está habilitado)</li>
            <li>Solo se cancelarán órdenes con estado "OPEN"</li>
          </ul>
        </div>

        <div className="alert alert-info">
          <h6 className="mb-2">ℹ️ Casos de Uso</h6>
          <Row>
            <Col md={4}>
              <strong>Todas (ALL):</strong>
              <p className="mb-0"><small>Cancela todas las órdenes abiertas del usuario. Útil para detener completamente la actividad.</small></p>
            </Col>
            <Col md={4}>
              <strong>Solo COMPRA:</strong>
              <p className="mb-0"><small>Cancela únicamente las órdenes de compra. Útil cuando hay demasiada exposición en compras.</small></p>
            </Col>
            <Col md={4}>
              <strong>Solo VENTA:</strong>
              <p className="mb-0"><small>Cancela únicamente las órdenes de venta. Útil para liberar activos en órdenes de venta.</small></p>
            </Col>
          </Row>
        </div>
      </CardBody>
    </Card>
  );
}

export default UserOrdersControl;